import { Question, QBTestQuestionEto } from '../lib/api/types';
import { Question as WhiteboardQuestion } from '../types/whiteboardProps';

/**
 * Question utility functions for managing test questions
 */

/**
 * Parse question data from API response
 */
export function parseQuestionData(
  questionEto: QBTestQuestionEto | undefined
): WhiteboardQuestion | null {
  if (!questionEto?.questionId) return null;

  // Map QBTestQuestionEto to our WhiteboardQuestion interface
  return {
    id: questionEto.questionId,
    questionId: questionEto.questionId,
    questionOrder: questionEto.questionOrder || 0,
    imageUrl: questionEto.imageUrl || '',
    answer: questionEto.answer || '',
    answerType: questionEto.answerType || 0,
    isCommonText: questionEto.isCommonText,
    parentQuestionImageUrl: questionEto.parentQuestionImageUrl || undefined,
    parentQuestionUrl: questionEto.parentQuestionImageUrl || undefined,
    href: questionEto.href || undefined,
    
    // Additional properties for compatibility
    choices: [],
    correctAnswer: questionEto.answer || undefined,
    explanation: '',
    opticalChoices: undefined
  };
}

/**
 * Calculate optimal scale for question image
 */
export function calculateQuestionScale(
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  padding: number = 20
): number {
  const availableWidth = canvasWidth - (padding * 2);
  const availableHeight = canvasHeight - (padding * 2);

  const scaleX = availableWidth / imageWidth;
  const scaleY = availableHeight / imageHeight;

  return Math.min(scaleX, scaleY, 1); // Don't scale up
}

/**
 * Get question image URL with cache busting
 */
export function getQuestionImageUrl(
  baseUrl: string | undefined,
  bustCache: boolean = true
): string | undefined {
  if (!baseUrl) return undefined;

  if (bustCache) {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}t=${Date.now()}`;
  }

  return baseUrl;
}

/**
 * Check if question has video content
 */
export function hasVideoContent(question: Question | WhiteboardQuestion | null): boolean {
  const q = question as any;
  return !!(q?.href || q?.videoUrl);
}

/**
 * Check if question has optical form
 */
export function hasOpticalForm(question: Question | WhiteboardQuestion | null): boolean {
  return !!(question?.opticalChoices);
}

/**
 * Get optical choice label (A, B, C, D, E)
 */
export function getOpticalChoiceLabel(index: number): string {
  const labels = ['A', 'B', 'C', 'D', 'E'];
  return labels[index] || '';
}

/**
 * Parse optical choice from label
 */
export function parseOpticalChoice(label: string): number {
  const labels = ['A', 'B', 'C', 'D', 'E'];
  return labels.indexOf(label.toUpperCase());
}

/**
 * Calculate question navigation info
 */
export function getQuestionNavigationInfo(
  currentIndex: number,
  totalQuestions: number
): {
  current: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
  progress: number;
} {
  return {
    current: currentIndex + 1,
    total: totalQuestions,
    hasNext: currentIndex < totalQuestions - 1,
    hasPrevious: currentIndex > 0,
    progress: totalQuestions > 0 ? ((currentIndex + 1) / totalQuestions) * 100 : 0
  };
}

/**
 * Group questions by topic
 */
export function groupQuestionsByTopic(
  questions: WhiteboardQuestion[]
): Record<string, WhiteboardQuestion[]> {
  return questions.reduce((groups, question) => {
    const topic = 'Genel'; // Default topic since topic property doesn't exist
    if (!groups[topic]) {
      groups[topic] = [];
    }
    groups[topic].push(question);
    return groups;
  }, {} as Record<string, WhiteboardQuestion[]>);
}

/**
 * Filter questions by difficulty
 */
export function filterQuestionsByDifficulty(
  questions: WhiteboardQuestion[],
  _difficulty: 'easy' | 'medium' | 'hard'
): WhiteboardQuestion[] {
  // Difficulty property doesn't exist, return all questions
  return questions;
}

/**
 * Calculate question statistics
 */
export function calculateQuestionStats(questions: WhiteboardQuestion[]): {
  total: number;
  byDifficulty: Record<string, number>;
  byTopic: Record<string, number>;
  withVideo: number;
  withOptical: number;
} {
  const stats = {
    total: questions.length,
    byDifficulty: { easy: 0, medium: 0, hard: 0 },
    byTopic: {} as Record<string, number>,
    withVideo: 0,
    withOptical: 0
  };

  questions.forEach(question => {
    // Topic
    const topic = 'Genel'; // Default topic since topic property doesn't exist
    stats.byTopic[topic] = (stats.byTopic[topic] || 0) + 1;

    // Video
    const q = question as any;
    if (q.href || q.videoUrl) {
      stats.withVideo++;
    }

    // Optical
    if (question.opticalChoices) {
      stats.withOptical++;
    }
  });

  return stats;
}

/**
 * Create question storage key
 */
export function createQuestionStorageKey(
  testId: string,
  questionId: string,
  userId: string
): string {
  return `question_${testId}_${questionId}_${userId}`;
}

/**
 * Validate question data
 */
export function validateQuestion(question: any): question is Question {
  return !!(
    question &&
    typeof question === 'object' &&
    question.id &&
    (question.content || question.imageUrl)
  );
}